/**
 * Copyright (c) 2025-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { gql, useMutation } from '@apollo/client'
import { useCallback, useState, type FC } from 'react'
import Modal from 'react-bootstrap/Modal'
import { toast } from 'react-toastify'
import FormTogglePassword, { type FormTogglePasswordHandleSubmit } from './FormTogglePassword'

const UPDATE_USER_ACCOUNT_MUTATION = gql`
  mutation($input: UpdateUserInput!) {
    updateUserAccount(input: $input)
  }
`

export type Props = {
  component: (props: { onClick: () => void }) => JSX.Element
}

/**
 * TogglePasswordModal is described here.
 *
 * @example
 * ```jsx
 * <TogglePasswordModal />
 * ```
 *
 * @since 14/04/2025
 * @link https://react-styleguidist.js.org/docs/documenting.html
 */
const TogglePasswordModal: FC<Props> = (props) => {
  const [updateUserAccount] = useMutation(UPDATE_USER_ACCOUNT_MUTATION)
  const [showTogglePassword, setShowTogglePassword] = useState<boolean>(false)

  const toggleshowTogglePassword = useCallback(() => {
    setShowTogglePassword((prev) => !prev)
  }, [setShowTogglePassword])

  const onSubmit = useCallback<FormTogglePasswordHandleSubmit>(
    async (values, helpers) => {
      try {
        helpers.setSubmitting(true)
        await updateUserAccount({
          variables: {
            input: {
              id: 'me',
              password: values.password
            }
          }
        })
        toast('Sua senha foi alterada com sucesso!', { type: 'success' })
      } catch (error) {
        toast("Não foi possível alterar sua senha, tente novamente mais tarde", { type: 'error' })
        console.error('Error updating password:', error)
      } finally {
        helpers.setSubmitting(false)
        toggleshowTogglePassword()
      }
    },
    [updateUserAccount, toggleshowTogglePassword]
  )

  return (
    <>
      {props.component({ onClick: toggleshowTogglePassword })}
      <Modal show={showTogglePassword} onHide={toggleshowTogglePassword}>
        <Modal.Header closeButton style={{ border: 'none' }}>
          <Modal.Title>Alterar minha senha</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormTogglePassword
            onCancel={toggleshowTogglePassword}
            onSubmit={onSubmit}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}

export default TogglePasswordModal
